import React, { useState, useEffect } from "react";
import {
  Paper, Typography, TextField, Button, Box, makeStyles,
  CircularProgress, Divider, Chip, IconButton, Tooltip,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    padding: theme.spacing(3, 3, 6),
    background: theme.mode === "light"
      ? "linear-gradient(155deg, #f5f9ff 0%, #eef4ff 50%, #f7fbff 100%)"
      : "linear-gradient(155deg, #0f172a 0%, #111827 50%, #1f2937 100%)",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: theme.mode === "light" ? "#2563eb" : "#60a5fa",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(2),
    width: "fit-content",
    "&:hover": { textDecoration: "underline" },
  },
  pageTitle: {
    fontWeight: 800,
    fontSize: "1.6rem",
    color: theme.mode === "light" ? "#0f172a" : "#f8fafc",
    marginBottom: 4,
  },
  pageSubtitle: {
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    fontSize: "0.9rem",
    marginBottom: theme.spacing(3),
  },
  section: {
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.mode === "light" ? "rgba(226,232,240,0.8)" : "rgba(51,65,85,0.6)"}`,
    background: theme.mode === "light" ? "#ffffff" : "rgba(30,41,59,0.7)",
    boxShadow: theme.mode === "light"
      ? "0 4px 16px rgba(15,23,42,0.06)"
      : "0 4px 16px rgba(0,0,0,0.2)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: theme.spacing(2),
  },
  sectionIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.mode === "light" ? "#0f172a" : "#f8fafc",
  },
  sectionDesc: {
    fontSize: "0.78rem",
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    marginTop: 1,
  },
  statusChip: {
    marginLeft: "auto",
    fontWeight: 700,
    fontSize: "0.65rem",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.mode === "light" ? "#f8fafc" : "rgba(15,23,42,0.5)",
    },
  },
  webhookBox: {
    background: theme.mode === "light" ? "#f0f9ff" : "rgba(30,58,138,0.2)",
    border: `1px solid ${theme.mode === "light" ? "rgba(37,99,235,0.25)" : "rgba(96,165,250,0.3)"}`,
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  webhookUrl: {
    fontSize: "0.75rem",
    fontFamily: "monospace",
    color: theme.mode === "light" ? "#1d4ed8" : "#93c5fd",
    wordBreak: "break-all",
    flex: 1,
  },
  infoBox: {
    background: theme.mode === "light" ? "#fffbeb" : "rgba(120,53,15,0.2)",
    border: `1px solid ${theme.mode === "light" ? "rgba(245,158,11,0.35)" : "rgba(245,158,11,0.3)"}`,
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1.5),
  },
  infoText: {
    fontSize: "0.78rem",
    color: theme.mode === "light" ? "#92400e" : "#fcd34d",
    lineHeight: 1.6,
  },
  saveBtn: {
    borderRadius: 10,
    fontWeight: 700,
    padding: "9px 24px",
    marginTop: theme.spacing(2),
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    },
  },
  stepBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50%",
    fontSize: "0.65rem",
    fontWeight: 800,
    color: "#fff",
    marginRight: 6,
    flexShrink: 0,
  },
  stepList: {
    listStyle: "none",
    padding: 0,
    margin: theme.spacing(1, 0, 0),
    "& li": {
      display: "flex",
      alignItems: "flex-start",
      fontSize: "0.78rem",
      color: theme.mode === "light" ? "#475569" : "#94a3b8",
      marginBottom: 6,
      lineHeight: 1.5,
    },
  },
}));

const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(":3000", ":8080");

const MetaAPI = () => {
  const classes = useStyles();
  const history = useHistory();

  // WhatsApp Cloud API
  const [waPhoneNumberId, setWaPhoneNumberId] = useState("");
  const [waWabaId, setWaWabaId] = useState("");
  const [waAccessToken, setWaAccessToken] = useState("");
  const [waWebhookVerifyToken, setWaWebhookVerifyToken] = useState("");
  const [waSaving, setWaSaving] = useState(false);

  // Facebook / Instagram
  const [fbAppId, setFbAppId] = useState("");
  const [fbAppSecret, setFbAppSecret] = useState("");
  const [fbSaving, setFbSaving] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/global-config");
        if (data.metaWaPhoneNumberId) setWaPhoneNumberId(data.metaWaPhoneNumberId);
        if (data.metaWaWabaId) setWaWabaId(data.metaWaWabaId);
        if (data.metaWaAccessToken) setWaAccessToken(data.metaWaAccessToken);
        if (data.metaWaWebhookVerifyToken) setWaWebhookVerifyToken(data.metaWaWebhookVerifyToken);
        if (data.metaFbAppId) setFbAppId(data.metaFbAppId);
        if (data.metaFbAppSecret) setFbAppSecret(data.metaFbAppSecret);
      } catch (err) {
        // first time, no config yet
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveWA = async () => {
    if (!waPhoneNumberId || !waAccessToken || !waWebhookVerifyToken) {
      toast.warning("Preencha Phone Number ID, Access Token e Webhook Verify Token.");
      return;
    }
    setWaSaving(true);
    try {
      await api.put("/global-config", {
        metaWaPhoneNumberId: waPhoneNumberId,
        metaWaWabaId: waWabaId,
        metaWaAccessToken: waAccessToken,
        metaWaWebhookVerifyToken: waWebhookVerifyToken,
      });
      toast.success("Configurações do WhatsApp Business API salvas!");
    } catch (err) {
      toastError(err);
    }
    setWaSaving(false);
  };

  const handleSaveFB = async () => {
    if (!fbAppId || !fbAppSecret) {
      toast.warning("Preencha o App ID e o App Secret.");
      return;
    }
    setFbSaving(true);
    try {
      await api.put("/global-config", {
        metaFbAppId: fbAppId,
        metaFbAppSecret: fbAppSecret,
      });
      toast.success("Configurações do Facebook/Instagram salvas!");
    } catch (err) {
      toastError(err);
    }
    setFbSaving(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado!"));
  };

  const waWebhookUrl = `${backendUrl}/webhook/meta/whatsapp`;
  const fbWebhookUrl = `${backendUrl}/webhook/meta/facebook`;

  const waConfigured = !!(waPhoneNumberId && waAccessToken && waWebhookVerifyToken);
  const fbConfigured = !!(fbAppId && fbAppSecret);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.backBtn} onClick={() => history.push("/configuracoes")}>
        <ArrowBackIcon style={{ fontSize: 16 }} />
        Voltar para Configurações
      </div>

      <Typography className={classes.pageTitle}>Integrações Meta</Typography>
      <Typography className={classes.pageSubtitle}>
        Configure a API Oficial do Meta para WhatsApp Business, Facebook e Instagram.
      </Typography>

      {/* ── WhatsApp Business API ── */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ background: "linear-gradient(135deg, #25D366, #128C7E)" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className={classes.sectionTitle}>WhatsApp Business API (Oficial)</div>
            <div className={classes.sectionDesc}>API oficial da Meta — sem risco de banimento, ideal para alto volume</div>
          </div>
          {waConfigured && (
            <Chip
              icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
              label="Configurado"
              size="small"
              className={classes.statusChip}
              style={{ background: "#dcfce7", color: "#16a34a" }}
            />
          )}
        </div>

        <Divider style={{ margin: "0 0 16px" }} />

        {/* How to get credentials */}
        <div className={classes.infoBox}>
          <Typography style={{ fontWeight: 700, fontSize: "0.78rem", marginBottom: 6, color: "inherit" }}>
            📋 Como obter as credenciais:
          </Typography>
          <ol className={classes.stepList}>
            <li>
              <span className={classes.stepBadge} style={{ background: "#f59e0b" }}>1</span>
              Acesse <strong>developers.facebook.com</strong> → Meus Apps → Criar App → Tipo: <em>Negócios</em>
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#f59e0b" }}>2</span>
              Adicione o produto <strong>WhatsApp</strong> ao seu App
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#f59e0b" }}>3</span>
              Em <em>WhatsApp → Configuração da API</em>, copie o <strong>Phone Number ID</strong> e o <strong>ID da Conta Comercial (WABA)</strong>
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#f59e0b" }}>4</span>
              Gere um <strong>Token de acesso permanente</strong> em Gerenciador de Sistemas
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#f59e0b" }}>5</span>
              Em <em>Webhooks</em>, use a URL abaixo e o seu Verify Token
            </li>
          </ol>
        </div>

        <div style={{ marginTop: 16, marginBottom: 4 }}>
          <Typography style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
            URL DO WEBHOOK (cole no Meta Developer Portal):
          </Typography>
          <div className={classes.webhookBox}>
            <span className={classes.webhookUrl}>{waWebhookUrl}</span>
            <Tooltip title="Copiar URL">
              <IconButton size="small" onClick={() => copyToClipboard(waWebhookUrl)}>
                <FileCopyIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div className={classes.fieldGroup}>
          <TextField
            label="Phone Number ID"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={waPhoneNumberId}
            onChange={(e) => setWaPhoneNumberId(e.target.value)}
            placeholder="Ex: 123456789012345"
            helperText="Encontrado em WhatsApp → Configuração da API → Phone Number ID"
          />
          <TextField
            label="WhatsApp Business Account ID (WABA)"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={waWabaId}
            onChange={(e) => setWaWabaId(e.target.value)}
            placeholder="Ex: 987654321098765"
            helperText="Opcional — ID da sua conta comercial do WhatsApp"
          />
          <TextField
            label="Access Token (Token de Acesso Permanente)"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={waAccessToken}
            onChange={(e) => setWaAccessToken(e.target.value)}
            placeholder="EAAxxxxxxxx..."
            type="password"
            helperText="Token gerado no Gerenciador de Sistemas da Meta"
          />
          <TextField
            label="Webhook Verify Token (você define)"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={waWebhookVerifyToken}
            onChange={(e) => setWaWebhookVerifyToken(e.target.value)}
            placeholder="Ex: meu_token_secreto_123"
            helperText="Crie uma senha qualquer — use a mesma no campo Verify Token do Meta Developer Portal"
          />
        </div>

        <Button
          className={classes.saveBtn}
          onClick={handleSaveWA}
          disabled={waSaving}
          variant="contained"
          disableElevation
        >
          {waSaving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "💾 Salvar WhatsApp Business API"}
        </Button>
      </Paper>

      {/* ── Facebook + Instagram ── */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ background: "linear-gradient(135deg, #1877F2, #0C63D4)" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className={classes.sectionTitle}>Facebook Messenger + Instagram</div>
            <div className={classes.sectionDesc}>Receba e responda mensagens do Facebook e Instagram direto no sistema</div>
          </div>
          {fbConfigured && (
            <Chip
              icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
              label="Configurado"
              size="small"
              className={classes.statusChip}
              style={{ background: "#dbeafe", color: "#1d4ed8" }}
            />
          )}
        </div>

        <Divider style={{ margin: "0 0 16px" }} />

        <div className={classes.infoBox}>
          <Typography style={{ fontWeight: 700, fontSize: "0.78rem", marginBottom: 6, color: "inherit" }}>
            📋 Como configurar Facebook e Instagram:
          </Typography>
          <ol className={classes.stepList}>
            <li>
              <span className={classes.stepBadge} style={{ background: "#1877F2" }}>1</span>
              Acesse <strong>developers.facebook.com</strong> → Meus Apps → Criar App → Tipo: <em>Negócios</em>
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#1877F2" }}>2</span>
              Adicione os produtos <strong>Messenger</strong> e <strong>Instagram Graph API</strong>
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#1877F2" }}>3</span>
              Copie o <strong>ID do App</strong> e o <strong>Chave Secreta do App</strong> (Configurações → Básico)
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#1877F2" }}>4</span>
              Em Webhooks, adicione a URL abaixo com o token de verificação da sua conta
            </li>
            <li>
              <span className={classes.stepBadge} style={{ background: "#E1306C" }}>5</span>
              Para Instagram: sua conta precisa ser <strong>Business ou Creator</strong> e vinculada a uma Página do Facebook
            </li>
          </ol>
        </div>

        <div style={{ marginTop: 16, marginBottom: 4 }}>
          <Typography style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
            URL DO WEBHOOK FACEBOOK/INSTAGRAM:
          </Typography>
          <div className={classes.webhookBox}>
            <span className={classes.webhookUrl}>{fbWebhookUrl}</span>
            <Tooltip title="Copiar URL">
              <IconButton size="small" onClick={() => copyToClipboard(fbWebhookUrl)}>
                <FileCopyIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div className={classes.fieldGroup}>
          <TextField
            label="Facebook App ID"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={fbAppId}
            onChange={(e) => setFbAppId(e.target.value)}
            placeholder="Ex: 1234567890123456"
            helperText="Encontrado em Configurações → Básico do seu App"
          />
          <TextField
            label="Facebook App Secret (Chave Secreta)"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            value={fbAppSecret}
            onChange={(e) => setFbAppSecret(e.target.value)}
            placeholder="Ex: abc123def456..."
            type="password"
            helperText="Chave secreta do App — nunca compartilhe com ninguém"
          />
        </div>

        <div className={classes.infoBox} style={{ marginTop: 12 }}>
          <Typography className={classes.infoText}>
            <strong>Após salvar:</strong> Vá em <strong>Configurações → Conexões WhatsApp</strong> e clique em <em>Nova Conexão</em>.<br/>
            Para conectar uma <strong>Página do Facebook</strong> ou conta do <strong>Instagram</strong>, será necessário adicionar o Token de Acesso da Página naquele fluxo.
          </Typography>
        </div>

        <Button
          className={classes.saveBtn}
          onClick={handleSaveFB}
          disabled={fbSaving}
          variant="contained"
          disableElevation
          style={{ background: "linear-gradient(135deg, #1877F2, #0C63D4)" }}
        >
          {fbSaving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "💾 Salvar Facebook & Instagram"}
        </Button>
      </Paper>

      {/* ── Nota informativa ── */}
      <Paper className={classes.section} elevation={0} style={{ background: "transparent", border: "1px dashed rgba(148,163,184,0.4)" }}>
        <Typography style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 8 }}>
          ℹ️ Diferença entre os dois tipos de conexão
        </Typography>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Chip label="QR Code (atual)" size="small" style={{ background: "#fef9c3", color: "#854d0e", fontWeight: 700, marginBottom: 8 }} />
            <Typography style={{ fontSize: "0.75rem", color: "#64748b", lineHeight: 1.6 }}>
              Simula o WhatsApp Web. Rápido de configurar, sem custo adicional. Pode ter instabilidades e risco de banimento em volumes altos.
            </Typography>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Chip label="API Oficial Meta" size="small" style={{ background: "#dcfce7", color: "#15803d", fontWeight: 700, marginBottom: 8 }} />
            <Typography style={{ fontSize: "0.75rem", color: "#64748b", lineHeight: 1.6 }}>
              Conexão oficial aprovada pela Meta. Estável, segura, sem risco de banimento. Custo por conversa (~R$0,20–0,80). Ideal para empresas.
            </Typography>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default MetaAPI;
